import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useAuth } from '@/contexts/AuthContext';
import { usePropertySubscription } from '@/contexts/PropertySubscriptionContext';
import { useLanguage } from '@/i18n';
import { FmButton, FmEmpty, FmSpinner, FmText, useFmToast } from '@/components/ui';

import { appRoutePatterns } from '@/shared/navigation/appRoutePatterns';
export type FeatureTier = 'free' | 'plus' | 'pro';

export interface PropertyAccessGuardProps {
  children: React.ReactNode;
  requiredTier: FeatureTier;
  featureName: string;
  description?: string;
}

type TierHierarchy = Record<FeatureTier, number>;

const TIER_HIERARCHY: TierHierarchy = { free: 0, plus: 1, pro: 2 };

interface RenderHelperParams {
  ag: ReturnType<typeof useLanguage>['t']['common']['propertyAccessGuard'];
  t: ReturnType<typeof useLanguage>['t'];
}

function renderLoadingSpinner(params: RenderHelperParams) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '50vh',
      padding: 'var(--fm-space-5)'
    }}>
      <FmSpinner size="lg" />
      <span style={{ marginLeft: 'var(--fm-space-4)', fontSize: 'var(--fm-fs-body)' }}>
        {params.ag.checkingAccess}
      </span>
    </div>
  );
}

interface SubscriptionFailedParams extends RenderHelperParams {
  onRetry: () => void;
  onBackToMap: () => void;
}

function renderSubscriptionFailed(params: SubscriptionFailedParams) {
  return (
    <FmEmpty
      icon={<WarningOutlined />}
      description={
        <>
          <FmText weight="bold">{params.ag.couldNotVerify}</FmText>
          <FmText size="caption" tone="muted">{params.ag.failedToLoadSubscription}</FmText>
        </>
      }
      action={[
        <FmButton variant="primary" size="md" key="retry" onClick={params.onRetry}>
          {params.ag.tryAgain}
        </FmButton>,
        <FmButton size="md" key="map" onClick={params.onBackToMap}>
          {params.t.common.backToMap}
        </FmButton>,
      ]}
    />
  );
}

interface PropertyNotAccessibleParams extends RenderHelperParams {
  onMyForests: () => void;
  onBackToMap: () => void;
}

function renderPropertyNotAccessible(params: PropertyNotAccessibleParams) {
  return (
    <FmEmpty
      data-testid="property-access-denied"
      icon={<CloseCircleOutlined />}
      description={
        <>
          <FmText weight="bold">{params.ag.propertyNotAccessible}</FmText>
          <FmText size="caption" tone="muted">{params.ag.noAccessDescription}</FmText>
        </>
      }
      action={[
        <FmButton variant="primary" size="md" key="my-forests" onClick={params.onMyForests}>
          {params.t.common.sidebar.myForests}
        </FmButton>,
        <FmButton size="md" key="map" onClick={params.onBackToMap}>
          {params.t.common.backToMap}
        </FmButton>,
      ]}
    />
  );
}

interface UpgradePromptParams extends RenderHelperParams {
  requiredTier: FeatureTier;
  featureName: string;
  description: string | undefined;
  onUpgrade: () => void;
  onBack: () => void;
}

function renderUpgradePrompt(params: UpgradePromptParams) {
  const localizedTierName = params.t.common.upgradePrompt.tierShortNames[params.requiredTier as 'plus' | 'pro'] || params.requiredTier;
  const defaultDescription = params.ag.defaultDescription
    .replace('{{featureName}}', params.featureName)
    .replace('{{tier}}', localizedTierName);
  
  return (
    <FmEmpty
      data-testid="upgrade-prompt"
      icon={<WarningOutlined />}
      description={
        <>
          <FmText weight="bold">{params.description || defaultDescription}</FmText>
          <FmText size="caption" tone="muted">{params.ag.upgradeHint}</FmText>
        </>
      }
      action={[
        <FmButton variant="primary" size="md" key="settings" onClick={params.onUpgrade}>
          {params.ag.upgradeSubscription}
        </FmButton>,
        <FmButton size="md" key="back" onClick={params.onBack}>
          {params.t.common.backToMap}
        </FmButton>,
      ]}
    />
  );
}

const PropertyAccessGuard: React.FC<PropertyAccessGuardProps> = ({
  children,
  requiredTier,
  featureName,
  description
}) => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t } = useLanguage();
  const toast = useFmToast();
  const ag = t.common.propertyAccessGuard;
  const {
    propertySubscriptions,
    isLoading: subscriptionsLoading,
    hasAttemptedLoad,
    error: subscriptionsError,
    refreshSubscriptions,
    getPropertySubscription,
  } = usePropertySubscription();

  const [isLoading, setIsLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [isPropertyOwned, setIsPropertyOwned] = useState(false);
  const [subscriptionLoadFailed, setSubscriptionLoadFailed] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      setIsLoading(true);

      // Redirect to login if not authenticated
      if (!isAuthenticated) {
        toast.info(ag.loginRequired);
        navigate(appRoutePatterns.map);
        return;
      }

      // Redirect if no property ID
      if (!propertyId) {
        toast.error(ag.propertyIdRequired);
        navigate(appRoutePatterns.map);
        return;
      }

      // Admin/superuser bypass: full access to all tiers
      if (user?.user_type === 'admin') {
        setHasAccess(true);
        setIsLoading(false);
        return;
      }

      // Wait for the subscription context to finish its initial load for the
      // current auth session before making an access decision.
      if (subscriptionsLoading || !hasAttemptedLoad) {
        return;
      }

      // If subscription data failed to load, we cannot determine ownership.
      // Show a retry page rather than incorrectly showing 403 or upgrade prompt.
      if (subscriptionsError && propertySubscriptions.length === 0) {
        setSubscriptionLoadFailed(true);
        setHasAccess(false);
        setIsLoading(false);
        return;
      }

      setSubscriptionLoadFailed(false);

      // Check if user has subscription for this property
      const subscription = getPropertySubscription(propertyId);

      if (!subscription) {
        // No subscription at all: user does not own this property.
        // Show a property-not-accessible page, NOT an upgrade prompt.
        setIsPropertyOwned(false);
        setHasAccess(false);
      } else if (subscription.is_expired || subscription.is_active === false) {
        // Retained forests keep their free read surface while expired package
        // access cannot satisfy any paid tier.
        setIsPropertyOwned(true);
        setHasAccess(requiredTier === 'free');
      } else {
        // User owns this property — check tier
        setIsPropertyOwned(true);
        const userTier = subscription.tier;

        // Check if user's tier meets the requirement
        const userTierLevel = TIER_HIERARCHY[userTier as FeatureTier] || 0;
        const requiredTierLevel = TIER_HIERARCHY[requiredTier];

        setHasAccess(userTierLevel >= requiredTierLevel);
      }

      setIsLoading(false);
    };

    checkAccess();
  }, [
    isAuthenticated,
    user,
    propertyId,
    propertySubscriptions,
    subscriptionsLoading,
    hasAttemptedLoad,
    subscriptionsError,
    requiredTier,
    navigate,
    getPropertySubscription,
    toast,
    ag.loginRequired,
    ag.propertyIdRequired,
  ]);

  // Show loading spinner while checking access
  if (isLoading || subscriptionsLoading) {
    return renderLoadingSpinner({ ag, t });
  }

  // Subscription data could not be loaded — show retry page instead of false 403
  if (subscriptionLoadFailed) {
    return renderSubscriptionFailed({
      ag,
      t,
      onRetry: () => refreshSubscriptions(),
      onBackToMap: () => navigate(appRoutePatterns.map),
    });
  }

  // Show access denied if user does not own this property at all
  if (!hasAccess && !isPropertyOwned) {
    return renderPropertyNotAccessible({
      ag,
      t,
      onMyForests: () => navigate(appRoutePatterns.myForests),
      onBackToMap: () => navigate(appRoutePatterns.map),
    });
  }

  // Show upgrade prompt if user owns the property but needs a higher tier
  if (!hasAccess) {
    return renderUpgradePrompt({
      ag,
      t,
      requiredTier,
      featureName,
      description,
      onUpgrade: () => navigate(appRoutePatterns.settings),
      onBack: () => navigate(appRoutePatterns.map),
    });
  }

  // Access granted - render the protected content
  return children;
};

export default PropertyAccessGuard;
